/**
 * @fileoverview Wash credit claim landing — emailed C2C credit links point here.
 *
 * Wash credits are pre-bound by recipient email at send time; once the
 * recipient lands here, signs in (or creates an account using the same email),
 * /api/wash-credit/claim binds every pending balance for that email to the
 * user's account.
 */

import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { api, ApiError } from '../../src/api/client';
import { useAuth } from '../../src/context/AuthContext';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, type ColorTheme } from '../../src/utils/theme';

export default function ClaimCreditScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [status, setStatus] = useState<'idle' | 'claiming' | 'done' | 'none' | 'error'>('idle');
  const [claimed, setClaimed] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (status !== 'idle') return;
    setStatus('claiming');
    (async () => {
      try {
        const res = await api<{ claimed: number }>('/api/wash-credit/claim', { method: 'POST', body: {} });
        if (res.claimed > 0) { setClaimed(res.claimed); setStatus('done'); }
        else setStatus('none');
      } catch (e: any) {
        setErrorMsg(e instanceof ApiError ? e.message : 'Could not claim your credit');
        setStatus('error');
      }
    })();
  }, [authLoading, isAuthenticated, status]);

  return (
    <>
      <Stack.Screen options={{ title: 'Your Wash Credit' }} />
      <ScrollView style={{ backgroundColor: colors.background }} contentContainerStyle={styles.scroll}>
        <ContentContainer maxWidth={480}>
          <View style={styles.card}>
            <Ionicons name="card" size={56} color={colors.primary} style={{ alignSelf: 'center' }} />
            <Text style={styles.headline}>Wash credit</Text>

            {(!isAuthenticated && !authLoading) ? (
              <View>
                <Text style={styles.helper}>Sign in or create a free account using the email this gift was sent to. Your balance applies automatically.</Text>
                <Button title="Sign In" onPress={() => router.push('/login?returnTo=/redeem/credit')} style={styles.btn} />
                <Button title="Create Account" variant="secondary" onPress={() => router.push('/register?returnTo=/redeem/credit')} style={styles.btn} />
              </View>
            ) : status === 'claiming' || authLoading ? (
              <View style={{ alignItems: 'center', marginTop: spacing.lg }}>
                <ActivityIndicator color={colors.primary} />
                <Text style={[styles.helper, { marginTop: spacing.sm }]}>Claiming your credit…</Text>
              </View>
            ) : status === 'done' ? (
              <View>
                <View style={[styles.banner, { backgroundColor: '#dcfce7' }]}>
                  <Ionicons name="checkmark-circle" size={20} color="#16a34a" />
                  <Text style={[styles.bannerText, { color: '#15803d' }]}>
                    {claimed === 1 ? 'Credit claimed' : `${claimed} credits claimed`}
                  </Text>
                </View>
                <Text style={styles.helper}>Pull up to a wash bay and scan the QR. Your balance will apply automatically.</Text>
                <Button title="Go to RinsePay" onPress={() => router.replace('/user')} style={styles.btn} />
              </View>
            ) : status === 'none' ? (
              <View>
                <Text style={styles.helper}>No pending credit found for your account. If a friend sent you a gift, sign in using the same email address they used.</Text>
                <Button title="Go to RinsePay" onPress={() => router.replace('/user')} style={styles.btn} />
              </View>
            ) : (
              <View>
                <View style={[styles.banner, { backgroundColor: '#fee2e2' }]}>
                  <Text style={[styles.bannerText, { color: '#991b1b' }]}>{errorMsg ?? 'Something went wrong'}</Text>
                </View>
                <Button title="Try again" onPress={() => setStatus('idle')} style={styles.btn} />
              </View>
            )}
          </View>
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  scroll: { paddingVertical: spacing.xl, alignItems: 'center', flexGrow: 1, justifyContent: 'center' },
  card: {
    backgroundColor: c.surface, borderRadius: 16, padding: spacing.xl,
    width: '100%', shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 }, elevation: 2,
  },
  headline: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text, textAlign: 'center', marginTop: spacing.md },
  helper: { color: c.textSecondary, fontSize: fontSize.sm, textAlign: 'center', marginTop: spacing.md, marginBottom: spacing.md, lineHeight: 20 },
  banner: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: 8, marginVertical: spacing.sm, marginTop: spacing.lg },
  bannerText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any },
  btn: { marginTop: spacing.sm, width: '100%' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
